import { DropdownList, IconName, Theme } from '@infotech-soe/erp-ui-kit';
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import { useAppDispatch } from '../../../store/hooks';
import session from '../../../store/slices/session';
import AboutPreferencesDialog from '../preferences/AboutPreferencesDialog';
import PreferencesDialog from '../preferences/PreferencesDialog';

interface AccountMenuProps {
  open: boolean;
  referenceElement: HTMLElement | null;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

const messages = defineMessages({
  exit: {
    id: 'core.preferencesDialog.session.signOut',
    defaultMessage: 'Sign out',
  },
  preferences: {
    id: 'core.preferencesDialog.title',
    defaultMessage: 'Preferences',
  },
  about: {
    id: 'core.preferencesDialog.about',
    defaultMessage: 'About program',
  },
});

const useStyles = createUseStyles(({ colors }: Theme) => ({
  exit: { color: colors.warning },
  labelContainer: {
    composes: ['row', 'items-center'],
    minHeight: 24,
  },
}));

const enum AccountMenuItems {
  PREFERENCES,
  ABOUT,
  EXIT,
}

const AccountMenu = (props: AccountMenuProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const { open, referenceElement, onOpenChange } = props;
  const [openPreferencesDialog, setOpenPreferencesDialog] = useState(false);
  const [openAboutDialog, setOpenAboutDialog] = useState(false);

  const handleMenuClick = (value: AccountMenuItems) => {
    switch (value) {
      case AccountMenuItems.PREFERENCES:
        setOpenPreferencesDialog(true);
        break;
      case AccountMenuItems.ABOUT:
        setOpenAboutDialog(true);
        break;
      case AccountMenuItems.EXIT:
        dispatch(session.signOut());
        break;
    }
  };

  const options = useMemo(
    () => [
      {
        type: 'option',
        icon: 'params' as IconName,
        label: (
          <div className={classes.labelContainer}>
            {formatMessage(messages.preferences)}
          </div>
        ),
        value: AccountMenuItems.PREFERENCES,
      },
      {
        type: 'option',
        icon: 'info' as IconName,
        label: (
          <div className={classes.labelContainer}>
            {formatMessage(messages.about)}
          </div>
        ),
        value: AccountMenuItems.ABOUT,
      },
      {
        type: 'separator',
      },
      {
        type: 'option',
        icon: 'post-change' as IconName,
        label: (
          <div className={`${classes.labelContainer} ${classes.exit}`}>
            {formatMessage(messages.exit)}
          </div>
        ),
        value: AccountMenuItems.EXIT,
      },
    ],
    [classes.exit, classes.labelContainer, formatMessage]
  );

  return (
    <>
      <DropdownList
        offset={-5}
        open={open}
        options={options}
        placement="bottom"
        referenceElement={referenceElement}
        onClick={handleMenuClick}
        onOpenChange={onOpenChange}
      />
      <PreferencesDialog
        open={openPreferencesDialog}
        onClose={() => setOpenPreferencesDialog(false)}
      />
      <AboutPreferencesDialog
        open={openAboutDialog}
        onClose={() => setOpenAboutDialog(false)}
      />
    </>
  );
};

export default memo(AccountMenu);
